import {
  Component,
  inject,
  input,
  output,
  computed,
} from '@angular/core'
import {
  EntityDetails,
  EntityDetailsField,
  EntityTypeMetadata,
  Entity,
  AuditColumnField,
  EditableRow,
  getDetailsFieldLabel,
  entityDetailValueLists,
  auditColumnFields,
  auditColumns,
  effectiveDateColumns,
  effectiveDateFields,
} from './entity-metadata'
import { ColDef } from 'ag-grid-community'
import { validateEntityDetails } from './validation'
import { EntityManagementService } from './entity-management.service'
import { getOptionsFormatter } from '../shared/utilities/formatters'
import { EditableTableComponent, EditorSpec } from './editable-table.component'
import { SelectCellEditorComponent, SelectCellEditorParams } from './select-cell-editor.component'
import { Option } from '../shared/models/general'
import { ChangeSetDescriptor } from './entity-management-changes.service'

type ColumnList = readonly (keyof EntityDetails)[]

const editableDateColumns: ColumnList = ['effective_date', 'termination_date']

const alwaysVisibleColumns: ColumnList = [
  ...editableDateColumns,
  ...auditColumnFields,
]

const getTypeColumns = (metadata?: EntityTypeMetadata): readonly string[] =>
  metadata ? [...metadata.required, ...metadata.optional] : []

export const getId = (row: EditableRow<EntityDetails>): string => {
  const { entity_uid } = row
  if (row.new) {
    return `${entity_uid}\tnew`
  } else {
    return `${entity_uid}\t${(row.original?.effective_date ?? row.effective_date ?? '').slice(0, 10)}`
  }
}


@Component({
  selector: 'app-entity-details-editor',
  imports: [
    EditableTableComponent,
  ],
  template: `
    <app-editable-table
        [changeSetDescriptor]="changeSetDescriptor()"
        [spec]="editorSpec()"
        [maxNewRows]="1"
        [rows]="entityDetails()"
        (saveComplete)="saveComplete.emit($event)"
        (error)="error.emit($event)"
    />
  `,
})
export class EntityDetailsEditorComponent {
  private readonly entityManagementService = inject(EntityManagementService)

  readonly entity = input.required<Entity>()
  readonly url = input.required<string>()
  readonly entityTypeMetadata = input.required<EntityTypeMetadata>()
  readonly entityDetails = input.required<readonly EntityDetails[]>()

  readonly saveComplete = output()
  readonly error = output<Error>()

  protected changeSetDescriptor = computed((): ChangeSetDescriptor => ({
    url: this.url(),
    description: `${this.entity().entity_type_code} ${this.entity().entity_code})`,
  }))

  protected columns = computed((): ColDef[] => {
    const metadata = this.entityTypeMetadata()
    const fieldSet = new Set([...alwaysVisibleColumns, ...getTypeColumns(metadata)])
    const typeCode = metadata?.entity_type_code ?? 'CPN'

    const getSelectCellEditorProperties = (
      valueList: readonly string[],
    ): Partial<ColDef> => {
      const options = valueList.map((value): Option => ({
        display: value,
        value,
      }))
      const cellEditorParams: SelectCellEditorParams<EntityDetails> = { options }
      return {
        cellEditor: SelectCellEditorComponent,
        cellEditorParams,
        valueFormatter: getOptionsFormatter(options),
      }
    }
    const createColDef = (
      field: keyof EntityDetails,
      partial: Partial<ColDef> = {},
    ): ColDef | undefined=> {
      if (fieldSet.has(field)) {
        const valueList = entityDetailValueLists[metadata.entity_type_code][field as EntityDetailsField]
        return {
          field,
          ...partial,
          headerName: partial.headerName ?? getDetailsFieldLabel(field, typeCode),
          editable: !auditColumnFields.includes(field as AuditColumnField),
          ...valueList ? getSelectCellEditorProperties(valueList) : {},
        }
      }
    }

    return [
      ...effectiveDateFields.map(field => createColDef(field, effectiveDateColumns[field])),
      createColDef('entity_detail_value'),
      createColDef('entity_business_type'),
      createColDef('asset_name'),
      createColDef('min_limit', { type: 'number', width: 100 }),
      createColDef('max_limit', { type: 'number', width: 100 }),
      createColDef('oasis_number'),
      createColDef('mdma_weather_sensitive_flag', { type: 'boolean', width: 210 }),
      createColDef('miso_id'),
      ...auditColumnFields.map(field => createColDef(field, auditColumns[field])),
    ].filter((colDef): colDef is ColDef => Boolean(colDef))
  })

  protected readonly editorSpec = computed((): EditorSpec<EntityDetails> => {
    const metadata = this.entityTypeMetadata()

    return {
      getId,
      initNewRow: () => ({ entity_uid: this.entity().entity_uid }),
      columns: this.columns(),
      validate: rows => validateEntityDetails(metadata, getId, rows),
      save: rows => {
        const { entity_type_code, entity_uid } = this.entity()
        return this.entityManagementService.updateDetailsRows(entity_type_code, entity_uid, rows)
      },
    }
  })
}
